import Phaser from 'phaser';
import { PLAYER_COLORS, CHARGE_MIN_TIME, CHARGE_MAX_TIME } from '../config/game.config';

export enum PlayerEvents {
    HEALTH_CHANGED = 'healthChanged',
    CAPACITY_CHANGED = 'capacityChanged',
    DIED = 'died',
}

/**
 * Player - Managed entity for health and capacity logic
 */
export class Player extends Phaser.Physics.Arcade.Sprite {
    private _health: number;
    private _maxHealth: number;
    private _capacity: number;
    private _maxCapacity: number;
    private _regenRate: number;
    private _color: number;
    private _healthRing: Phaser.GameObjects.Graphics;
    private _isCharging: boolean = false;
    private _chargeStartTime: number = 0;
    private _baseX: number = 0;  // For trembling
    private _baseY: number = 0;
    private _isStunned: boolean = false;
    private _stunTimer: number = 0;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        texture: string,
        config: {
            health?: number;
            maxHealth?: number;
            capacity?: number;
            maxCapacity?: number;
            regenRate?: number;
            color?: number;
        } = {}
    ) {
        super(scene, x, y, texture);

        this.scene.add.existing(this);
        this.scene.physics.add.existing(this);

        this._maxHealth = config.maxHealth ?? 100;
        this._health = config.health ?? this._maxHealth;
        this._maxCapacity = config.maxCapacity ?? 100;
        this._capacity = config.capacity ?? this._maxCapacity;
        this._regenRate = config.regenRate ?? 5; // units per second
        this._color = config.color ?? Player.getOrCreatePlayerColor();

        this._healthRing = this.scene.add.graphics();
        this.setDepth(50);
        this._healthRing.setDepth(49);

        this.setCollideWorldBounds(true);
        this.setTint(this._color);
    }

    /**
     * Gets or creates a player color
     * @param index - Optional index for deterministic color assignment (useful for multiplayer)
     */
    public static getOrCreatePlayerColor(index?: number): number {
        // If index is provided, use it directly for deterministic color assignment
        if (index !== undefined) {
            return PLAYER_COLORS[index % PLAYER_COLORS.length];
        }

        // Fallback to localStorage for single-player or when no index is provided
        let color = localStorage.getItem('playerColor');
        if (!color) {
            const idx = Math.floor(Math.random() * PLAYER_COLORS.length);
            color = PLAYER_COLORS[idx].toString();
            localStorage.setItem('playerColor', color);
        }
        return parseInt(color);
    }

    get health(): number { return this._health; }
    set health(value: number) {
        if (this._health === value) return;
        this._health = value;
        this.emit(PlayerEvents.HEALTH_CHANGED, this._health, this._maxHealth);
        this.drawHealthRing();
    }
    get maxHealth(): number { return this._maxHealth; }
    get capacity(): number { return this._capacity; }
    get maxCapacity(): number { return this._maxCapacity; }
    get isStunned(): boolean { return this._isStunned; }
    get color(): number { return this._color; }
    get isCharging(): boolean { return this._isCharging; }

    getChargePercent(): number {
        if (!this._isCharging) return 0;

        const holdTime = Date.now() - this._chargeStartTime;
        if (holdTime < CHARGE_MIN_TIME) return 0;

        const normalized = Math.min(1,
            (holdTime - CHARGE_MIN_TIME) / (CHARGE_MAX_TIME - CHARGE_MIN_TIME)
        );
        return normalized; // Linear scaling is more predictable for users
    }

    startCharging(): void {
        if (this.isStunned || this.isDead()) return;

        this._isCharging = true;
        this._chargeStartTime = Date.now();
        this._baseX = this.x;
        this._baseY = this.y;
    }

    stopCharging(): number {
        const percent = this.getChargePercent();
        this._isCharging = false;
        this._chargeStartTime = 0;
        this.setTint(this._color); // Reset tint
        return percent;
    }

    /**
     * Update the base position for charging trembling effect.
     * Used for remote players whose position is updated via network sync.
     */
    updateChargeBasePosition(x: number, y: number): void {
        this._baseX = x;
        this._baseY = y;
    }

    stun(duration: number, shakeCamera: boolean = true): void {
        if (this._health <= 0 || this._isStunned) return;

        // Cancel charging if stunned
        if (this._isCharging) {
            this._isCharging = false;
            this._chargeStartTime = 0;
        }

        this._isStunned = true;
        this._stunTimer = duration;

        // Visual feedback for stun (tint to yellow-white)
        this.setTint(0xffff00);
        if (shakeCamera) {
            this.scene.cameras.main.shake(100, 0.005);
        }
        this.drawHealthRing();
    }

    clearStun(): void {
        this._isStunned = false;
        this._stunTimer = 0;
        this.setTint(this._color);
        this.drawHealthRing();
    }

    takeDamage(amount: number): void {
        if (this._health <= 0) return;

        this._health = Math.max(0, this._health - amount);
        this.emit(PlayerEvents.HEALTH_CHANGED, this._health, this._maxHealth);
        this.drawHealthRing();

        if (this._health === 0) {
            this.emit(PlayerEvents.DIED);
        }
    }

    heal(amount: number): void {
        if (this._health <= 0) return;

        this._health = Math.min(this._maxHealth, this._health + amount);
        this.emit(PlayerEvents.HEALTH_CHANGED, this._health, this._maxHealth);
        this.drawHealthRing();
    }

    useCapacity(amount: number): boolean {
        if (this._capacity < amount || this._isStunned) return false;

        this._capacity -= amount;
        this.emit(PlayerEvents.CAPACITY_CHANGED, this._capacity, this._maxCapacity);
        return true;
    }

    regenCapacity(delta: number): void {
        if (this._capacity >= this._maxCapacity) return;

        // delta is in ms, we want units per second
        const amount = (this._regenRate * delta) / 1000;
        this._capacity = Math.min(this._maxCapacity, this._capacity + amount);
        this.emit(PlayerEvents.CAPACITY_CHANGED, this._capacity, this._maxCapacity);
    }

    isDead(): boolean {
        return this._health <= 0;
    }

    update(time: number, delta: number): void {
        if (this.isDead()) return;

        if (this._isStunned) {
            this._stunTimer -= delta;
            if (this._stunTimer <= 0) {
                this._isStunned = false;
                this._stunTimer = 0;
                this.setTint(this._color);
            }
        }

        this.regenCapacity(delta);

        // Handle charging effects
        if (this._isCharging) {
            const chargePercent = this.getChargePercent();

            // Trembling - intensity increases with charge
            // Use baseX/baseY to prevent drifting and clamp within world bounds
            if (chargePercent > 0) {
                const intensity = chargePercent * 4; // Max 4 pixels jitter
                const jitterX = (Math.random() - 0.5) * intensity;
                const jitterY = (Math.random() - 0.5) * intensity;

                // Calculate new position with jitter
                let newX = this._baseX + jitterX;
                let newY = this._baseY + jitterY;

                // Clamp within physics world bounds if available
                const body = this.body as Phaser.Physics.Arcade.Body;
                if (body && this.scene.physics.world) {
                    const bounds = this.scene.physics.world.bounds;
                    const halfWidth = body.width / 2;
                    const halfHeight = body.height / 2;

                    newX = Phaser.Math.Clamp(newX, bounds.x + halfWidth, bounds.right - halfWidth);
                    newY = Phaser.Math.Clamp(newY, bounds.y + halfHeight, bounds.bottom - halfHeight);
                }

                this.x = newX;
                this.y = newY;
            }

            // Glow effect - tint shifts toward yellow/white
            const glowColor = Phaser.Display.Color.Interpolate.ColorWithColor(
                Phaser.Display.Color.ValueToColor(this._color),
                Phaser.Display.Color.ValueToColor(0xffff00), // Yellow
                100,
                chargePercent * 100
            );
            this.setTint(Phaser.Display.Color.GetColor(glowColor.r, glowColor.g, glowColor.b));
        } else if (!this._isStunned) {
            // Reset tint to normal color if not stunned
            this.setTint(this._color);
        }

        this.drawHealthRing();
    }

    private drawHealthRing(): void {
        if (!this._healthRing) return;

        this._healthRing.clear();

        if (this.isDead()) return;

        const percent = this._health / this._maxHealth;
        const radius = 18; // Slightly larger than player circle
        const thickness = 3;

        // Determine color
        let color = 0x00b894; // Green
        if (percent <= 0.25) color = 0xd63031; // Red
        else if (percent <= 0.5) color = 0xfdcb6e; // Yellow

        // Draw background arc (dark)
        this._healthRing.lineStyle(thickness, 0x000000, 0.3);
        this._healthRing.strokeCircle(this.x, this.y, radius);

        // Draw health arc
        if (percent > 0) {
            this._healthRing.lineStyle(thickness, color, 1);
            // Start from top (-90°), go clockwise
            const startAngle = -Math.PI / 2;
            const endAngle = startAngle + (Math.PI * 2 * percent);
            this._healthRing.beginPath();
            this._healthRing.arc(this.x, this.y, radius, startAngle, endAngle);
            this._healthRing.strokePath();
        }
    }

    destroy(fromScene?: boolean): void {
        if (this._healthRing) {
            this._healthRing.destroy();
        }
        super.destroy(fromScene);
    }
}
